/**
 * The account menu in the header, once someone is signed in: who they are,
 * and the handful of things there are to do about it. A dropdown built by
 * hand rather than pulled in as a dependency, since the site needs exactly
 * one of these.
 */

import { useRef, useState, type FocusEvent, type KeyboardEvent } from "react";
import { Link } from "react-router-dom";

import type { SessionUser } from "../auth/authApi";
import { SignOutButton } from "../auth/SignOutButton";

export function ProfileMenu({ user }: { user: SessionUser }) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);

  // Closes once focus leaves the menu for good — a click elsewhere, or
  // Tabbing out — rather than tracking outside clicks by hand.
  function onBlur(event: FocusEvent<HTMLDivElement>) {
    if (!event.currentTarget.contains(event.relatedTarget)) setOpen(false);
  }

  // Escape closes and returns focus to the trigger, so keyboard use doesn't
  // lose its place.
  function onKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    if (event.key === "Escape") {
      setOpen(false);
      triggerRef.current?.focus();
    }
  }

  return (
    <div className="profile-menu" onBlur={onBlur} onKeyDown={onKeyDown}>
      <button
        type="button"
        ref={triggerRef}
        className="profile-menu__trigger"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Account menu for ${user.email}`}
        onClick={() => setOpen((wasOpen) => !wasOpen)}
      >
        {user.email.charAt(0).toUpperCase()}
      </button>

      {open && (
        <div className="profile-menu__panel" role="menu">
          {/* A label, not a control — `role="presentation"` keeps it out of
              the way of whoever is arrowing through actual menu items. */}
          <p className="profile-menu__email" role="presentation">
            {user.email}
          </p>
          <Link
            to="/account"
            role="menuitem"
            className="profile-menu__item"
            onClick={() => setOpen(false)}
          >
            Profile
          </Link>
          <Link
            to="/notifications"
            role="menuitem"
            className="profile-menu__item"
            onClick={() => setOpen(false)}
          >
            Notifications
          </Link>
          <Link
            to="/download"
            role="menuitem"
            className="profile-menu__item"
            onClick={() => setOpen(false)}
          >
            Download
          </Link>
          <SignOutButton
            role="menuitem"
            className="profile-menu__item"
            onClick={() => setOpen(false)}
          />
        </div>
      )}
    </div>
  );
}
